import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { PiperRequest, PiperResult, PiperTtsApi, PiperVoice } from './piper-tts-api.service';

interface HistoryItem { createdAt:Date; result:PiperResult; audioUrl:string; }

@Component({
  selector:'app-piper-tts-lab', standalone:true, imports:[CommonModule,FormsModule],
  templateUrl:'./piper-tts-lab.component.html', styleUrls:['./piper-tts-lab.component.scss']
})
export class PiperTtsLabComponent implements OnInit, OnDestroy {
  status:any={state:'connecting'}; voices:PiperVoice[]=[]; language='ur'; voiceId='';
  text=''; runs=1; lengthScale=1; noiseScale=.667; noiseWScale=.8;
  loading=false; error=''; result:PiperResult|null=null; audioUrl=''; history:HistoryItem[]=[];
  readonly samples={
    ur:'السلام علیکم، میں صبا ہوں، کوجینٹ کی ورچوئل اسسٹنٹ۔ میں آپ کی کیا مدد کر سکتی ہوں؟',
    en:"Hello, I am SABA, COGENT's virtual assistant. How may I help you today?"
  };
  constructor(private api:PiperTtsApi) {}
  async ngOnInit() { await this.refresh(); }
  ngOnDestroy() { this.history.forEach(x=>URL.revokeObjectURL(x.audioUrl)); }
  get filteredVoices() { return this.voices.filter(v=>v.language.toLowerCase().startsWith(this.language)); }
  get selectedVoice() { return this.voices.find(v=>v.id===this.voiceId); }
  get ready() { return this.status?.state==='ready' && this.selectedVoice?.installed; }
  async refresh() {
    this.error='';
    try {
      const [status,voices]=await Promise.all([firstValueFrom(this.api.status()),firstValueFrom(this.api.voices())]);
      this.status=status; this.voices=voices;
      this.chooseLanguage(this.language, false);
    } catch(e:any) { this.status={state:'unavailable'}; this.error=this.message(e,'Piper lab is unavailable.'); }
  }
  chooseLanguage(value:string, replaceText=true) {
    this.language=value;
    const candidates=this.filteredVoices;
    if(!candidates.some(v=>v.id===this.voiceId)) this.voiceId=(candidates.find(v=>v.installed)||candidates[0])?.id||'';
    if(replaceText || !this.text) this.text=this.samples[value as 'ur'|'en'];
  }
  resetDefaults() { this.lengthScale=1; this.noiseScale=.667; this.noiseWScale=.8; }
  async synthesize() {
    if(this.loading||!this.ready||!this.text.trim()) return;
    this.loading=true; this.error='';
    const request:PiperRequest={voiceId:this.voiceId,text:this.text.trim(),runs:this.runs,
      lengthScale:this.lengthScale,noiseScale:this.noiseScale,noiseWScale:this.noiseWScale};
    try {
      const result=await firstValueFrom(this.api.synthesize(request));
      const bytes=Uint8Array.from(atob(result.audioBase64),c=>c.charCodeAt(0));
      const url=URL.createObjectURL(new Blob([bytes],{type:result.contentType}));
      this.result=result; this.audioUrl=url;
      this.history.unshift({createdAt:new Date(),result,audioUrl:url});
    } catch(e:any) { this.error=this.message(e,'Speech generation failed.'); }
    finally { this.loading=false; }
  }
  play(item:HistoryItem) { this.result=item.result; this.audioUrl=item.audioUrl; }
  download(item:HistoryItem) { const a=document.createElement('a'); a.href=item.audioUrl; a.download=`piper-${item.result.voice.id}-${item.createdAt.getTime()}.wav`; a.click(); }
  private message(e:any,fallback:string) { return e?.error?.detail || e?.error?.message || fallback; }
}
